import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { colorForTeacher, matchesTeacher } from "@/lib/teacherColors";
import { useI18n } from "@/i18n/i18n";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, BookOpen, ClipboardList, CalendarCheck, ExternalLink, Radio } from "lucide-react";
import { currentCycleDates } from "@/lib/schedule";
import CalendarWeek from "@/components/CalendarWeek";
import { useIsMobile } from "@/hooks/useIsMobile";
import { todayISO } from "@/lib/dateFormat";

const WEEKDAYS_ES = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const WEEKDAYS_EN = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const DOT_COLORS = {
  class: "bg-brand-blue",
  extra_session: "bg-purple-500",
  attendance: "bg-emerald-500",
  homework: "bg-orange-500",
  live_session: "bg-teal-500",
};

const pad = (n) => String(n).padStart(2, "0");
// Mediodía para que ningún cambio de zona mueva el día. El "hoy" real viene
// siempre de todayISO() (hora de Costa Rica), nunca del reloj del navegador.
const crToday = () => new Date(`${todayISO()}T12:00:00`);
const monthKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function buildCells(year, month) {
  // month is 0-indexed
  const first = new Date(year, month, 1);
  // Monday-first index: JS getDay() Sun=0..Sat=6 → shift to Mon=0..Sun=6
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function CalendarMonth() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const [view, setView] = useState(() => (searchParams.get("view") === "month" ? "month" : "week"));
  const [cursor, setCursor] = useState(() => {
    const fromUrl = searchParams.get("month"); // YYYY-MM
    if (fromUrl && /^\d{4}-\d{2}$/.test(fromUrl)) {
      const [y, m] = fromUrl.split("-").map(Number);
      return new Date(y, m - 1, 1);
    }
    const n = crToday();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });

  // Keep cursor in sync if ?month= changes (e.g. notification deep-link)
  useEffect(() => {
    const fromUrl = searchParams.get("month");
    if (fromUrl && /^\d{4}-\d{2}$/.test(fromUrl)) {
      const [y, m] = fromUrl.split("-").map(Number);
      const next = new Date(y, m - 1, 1);
      setCursor((prev) =>
        prev.getFullYear() === next.getFullYear() && prev.getMonth() === next.getMonth()
          ? prev
          : next
      );
    }
  }, [searchParams]);
  const [data, setData] = useState({ events: [], groups: [] });
  const [enrollments, setEnrollments] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [groupFilter, setGroupFilter] = useState("all");
  const [teacherFilter, setTeacherFilter] = useState("all");
  const [selectedDay, setSelectedDay] = useState(null);

  // Load enrollments + programs once for students (needed to compute cycle phase per class)
  useEffect(() => {
    if (user?.role !== "student") return;
    (async () => {
      const [e, p] = await Promise.all([api.get("/enrollments"), api.get("/programs")]);
      setEnrollments(e.data); setPrograms(p.data);
    })();
  }, [user?.role]);

  // Build per-group cycle classification (only for students)
  // Map<group_id, { totalPerCycle, currentCycle, cycleByDate: Map<isoDate, cycleIndex> }>
  const cycleByGroup = useMemo(() => {
    if (user?.role !== "student") return new Map();
    const map = new Map();
    const programsMap = Object.fromEntries(programs.map((p) => [p.id, p]));
    const hoyISO = todayISO();
    for (const enr of enrollments) {
      const group = data.groups.find((g) => g.id === enr.group_id);
      if (!group || (group.schedule_days || []).length === 0) continue;
      const prog = programsMap[enr.program_id];
      const totalPerCycle = enr.classes_included || prog?.classes_included || 8;
      // No attendance here — the shared helper assumes "no record → consumes",
      // which is the approximation this calendar needs.
      const cc = currentCycleDates({
        group, enrollment: enr, totalPerCycle, today: hoyISO,
      });
      if (!cc) continue;
      const cycleByDate = new Map(cc.classified.map((c) => [c.date, c.cycle]));
      map.set(enr.group_id, { totalPerCycle, currentCycle: cc.cycle, cycleByDate });
    }
    return map;
  }, [enrollments, programs, data.groups, user?.role]);

  const monthLabel = useMemo(() => {
    return cursor.toLocaleDateString(lang === "es" ? "es-ES" : "en-US", { month: "long", year: "numeric" });
  }, [cursor, lang]);

  const reload = async (d) => {
    const r = await api.get("/calendar", { params: { month: monthKey(d) } });
    setData(r.data);
  };
  useEffect(() => { reload(cursor); }, [cursor]);

  const WEEKDAYS = lang === "es" ? WEEKDAYS_ES : WEEKDAYS_EN;
  const cells = useMemo(() => buildCells(cursor.getFullYear(), cursor.getMonth()), [cursor]);

  const eventsByDate = useMemo(() => {
    const m = new Map();
    for (const e of data.events) {
      if (groupFilter !== "all" && e.group_id !== groupFilter) continue;
      if (!matchesTeacher(e, teacherFilter)) continue;
      const k = e.date;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(e);
    }
    return m;
  }, [data.events, groupFilter, teacherFilter]);

  const selectedEvents = selectedDay ? (eventsByDate.get(selectedDay) || []) : [];
  const hoyISO = todayISO();

  const prevMonth = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const nextMonth = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const goToday = () => { const n = crToday(); setCursor(new Date(n.getFullYear(), n.getMonth(), 1)); };

  const renderChip = (e, idx) => {
    if (e.type === "class") {
      // Cycle-based coloring (students only). Past cycle = muted, current = vivid, future = soft.
      let cycleClasses = "bg-brand-blue/10 text-brand-blue";
      let cyclePhase = null;
      if (user?.role === "student") {
        const info = cycleByGroup.get(e.group_id);
        if (info) {
          const evCycle = info.cycleByDate.get(e.date);
          if (evCycle != null) {
            if (evCycle < info.currentCycle) {
              cycleClasses = "bg-slate-100 text-slate-500 opacity-70"; // past cycle (muted)
              cyclePhase = "past";
            } else if (evCycle === info.currentCycle) {
              cycleClasses = "bg-brand-blue/15 text-brand-blue font-semibold ring-1 ring-brand-blue/30"; // current (vivid)
              cyclePhase = "current";
            } else {
              cycleClasses = "bg-purple-50 text-purple-700 border border-purple-200"; // future (soft)
              cyclePhase = "future";
            }
          }
        }
      }
      if (user?.role !== "student") cycleClasses = `border ${colorForTeacher(data.teachers, e.teacher_id)}`;
      return (
        <div
          key={idx}
          className={`text-[10px] px-1.5 py-0.5 rounded truncate ${cycleClasses}`}
          title={`${e.group_name}${e.schedule_start_time ? " " + e.schedule_start_time : ""}${e.teacher_name ? " · " + e.teacher_name : ""}${cyclePhase ? ` · ${t("cycle_" + cyclePhase)}` : ""}`}
          data-testid={cyclePhase ? `calendar-class-${e.date}-${cyclePhase}` : undefined}
        >
          📚 {e.group_name}
        </div>
      );
    }
    if (e.type === "extra_session") {
      const sType = e.session_type || "extra";
      const palette = sType === "reschedule"
        ? "bg-orange-100 text-orange-800 border border-orange-300"
        : sType === "makeup"
        ? "bg-amber-100 text-amber-800 border border-amber-300 border-dashed"
        : "bg-purple-100 text-purple-800 border border-purple-300";
      const icon = sType === "reschedule" ? "🔄" : sType === "makeup" ? "♻️" : "✨";
      const label = sType === "reschedule"
        ? t("extra_session_reschedule")
        : sType === "makeup"
        ? t("extra_session_makeup")
        : t("extra_session_extra");
      return (
        <div
          key={idx}
          className={`text-[10px] px-1.5 py-0.5 rounded truncate ${palette}`}
          title={`${label} · ${e.group_name}${e.schedule_start_time ? " " + e.schedule_start_time : ""}`}
          data-testid={`calendar-extra-${e.date}-${sType}`}
        >
          {icon} <span className="font-semibold">{label}</span> · {e.group_name}
        </div>
      );
    }
    if (e.type === "live_session") {
      const waiting = e.my_booking_status === "waitlisted";
      return (
        <div
          key={idx}
          className={`text-[10px] px-1.5 py-0.5 rounded truncate ${waiting ? "bg-amber-100 text-amber-800" : "bg-teal-100 text-teal-800"}`}
          title={`WSS Live · ${e.title} · ${e.start_time}–${e.end_time}${e.teacher_name ? " · " + e.teacher_name : ""}`}
          data-testid={`calendar-live-${e.date}`}
        >
          🎧 {e.start_time} {e.title}
        </div>
      );
    }
    if (e.type === "homework") {
      const cls = e.kind === "due"
        ? (e.completed ? "bg-emerald-100 text-emerald-800" : "bg-orange-100 text-orange-800")
        : "bg-slate-100 text-slate-700";
      const label = e.kind === "due" ? "⏰" : "📝";
      return (
        <div key={idx} className={`text-[10px] px-1.5 py-0.5 rounded truncate ${cls}`} title={e.title}>
          {label} {e.title}
        </div>
      );
    }
    if (e.type === "attendance") {
      // For student: highlight my_status. For admin/teacher: show counts.
      if (e.my_status) {
        const colors = {
          present: "bg-emerald-100 text-emerald-800",
          absent: "bg-red-100 text-red-800",
          justified: "bg-blue-100 text-blue-800",
          unjustified: "bg-yellow-100 text-yellow-800",
          teacher_cancelled: "bg-slate-200 text-slate-700",
        };
        const cls = colors[e.my_status] || "bg-slate-100 text-slate-700";
        return (
          <div key={idx} className={`text-[10px] px-1.5 py-0.5 rounded truncate ${cls}`}>
            ✓ {t(e.my_status)}
          </div>
        );
      }
      const total = Object.values(e.counts || {}).reduce((a, b) => a + b, 0);
      return (
        <div key={idx} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 truncate" title={`${e.group_name}: ${total}`}>
          ✓ {e.group_name} ({total})
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <PageHeader title={user?.role === "student" ? t("my_calendar") : t("calendar")} testId="calendar-header" />

      {view === "week" ? (
        <CalendarWeek onSwitchView={setView} groupFilter={groupFilter} groups={data.groups || []} onGroupFilterChange={setGroupFilter}
          teacherFilter={teacherFilter} teachers={data.teachers || []} onTeacherFilterChange={setTeacherFilter} />
      ) : (
        <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={prevMonth} data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <h2 className="font-display text-xl font-semibold text-slate-900 capitalize min-w-[180px] text-center" data-testid="calendar-month-label">
            {monthLabel}
          </h2>
          <Button variant="outline" size="icon" onClick={nextMonth} data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" size="sm" onClick={goToday} data-testid="calendar-today">{t("today")}</Button>
          <div className="ml-2 inline-flex rounded-lg overflow-hidden border border-slate-300">
            <button
              type="button"
              onClick={() => setView("month")}
              className="px-3 py-1.5 text-xs font-semibold bg-orange-500 text-white"
              data-testid="view-month-btn"
            >
              {t("view_month")}
            </button>
            <button
              type="button"
              onClick={() => setView("week")}
              className="px-3 py-1.5 text-xs font-medium bg-white text-slate-600 hover:bg-slate-50"
              data-testid="view-week-btn"
            >
              {t("view_week")}
            </button>
          </div>
        </div>
        {(data.teachers || []).length > 1 && user?.role !== "student" && (
          <select
            value={teacherFilter}
            onChange={(e) => setTeacherFilter(e.target.value)}
            className="text-sm border border-slate-200 rounded-md px-3 py-1.5 bg-white"
            data-testid="calendar-teacher-filter"
          >
            <option value="all">{t("all_teachers")}</option>
            {data.teachers.map((tc) => <option key={tc.id} value={tc.id}>{tc.name}</option>)}
          </select>
        )}
        {data.groups.length > 1 && (
          <select
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className="text-sm border border-slate-200 rounded-md px-3 py-1.5 bg-white"
            data-testid="calendar-group-filter"
          >
            <option value="all">{t("all_groups")}</option>
            {data.groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        )}
      </div>

      <Card className="overflow-hidden">
        <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
          {WEEKDAYS.map((w) => (
            <div key={w} className="px-2 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold text-center">{w}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            const dISO = d ? isoDate(d) : null;
            const dayEvents = dISO ? (eventsByDate.get(dISO) || []) : [];
            const isToday = dISO === hoyISO;
            const isSelected = dISO === selectedDay;
            const hasScheduledClass = dayEvents.some((e) => e.type === "class");
            const hasReportedAttendance = dayEvents.some((e) => e.type === "attendance");
            const isPast = dISO && dISO < hoyISO;
            // Nudge: scheduled class in the past without attendance reported (only relevant
            // for teacher/admin views where we can see counts).
            const missingReport = user?.role !== "student" && hasScheduledClass && !hasReportedAttendance && isPast;
            return (
              <button
                key={i}
                type="button"
                onClick={() => d && setSelectedDay(dISO)}
                disabled={!d}
                data-testid={d ? `calendar-day-${dISO}` : undefined}
                className={`relative ${isMobile ? "min-h-[58px]" : "min-h-[88px]"} p-1.5 border-r border-b border-slate-200 text-left transition-colors ${
                  !d ? "bg-slate-50/50" : "hover:bg-slate-50"
                } ${isSelected ? "bg-brand-blue/5 ring-1 ring-brand-blue/30" : ""} ${
                  missingReport ? "bg-red-50 ring-1 ring-red-300" : ""
                }`}
              >
                {d && (
                  <>
                    <div className="flex items-center justify-between mb-1">
                      <div className={`text-[11px] font-semibold ${
                        isToday ? "bg-brand text-white rounded-full w-5 h-5 flex items-center justify-center"
                        : "text-slate-700"
                      }`}>
                        {d.getDate()}
                      </div>
                      {missingReport && (
                        <span
                          className="text-[10px] font-bold text-red-700 bg-red-100 rounded-full w-4 h-4 flex items-center justify-center"
                          data-testid={`calendar-missing-${dISO}`}
                          title={t("missing_report")}
                        >
                          !
                        </span>
                      )}
                    </div>
                    <div className="space-y-0.5">
                      {isMobile ? (
                        dayEvents.length > 0 && (
                          <div className="flex flex-wrap gap-0.5" data-testid={`calendar-dots-${dISO}`}>
                            {dayEvents.slice(0, 6).map((e, di) => (
                              <span key={di} className={`h-1.5 w-1.5 rounded-full ${DOT_COLORS[e.type] || "bg-slate-400"}`} />
                            ))}
                          </div>
                        )
                      ) : (
                        <>
                          {dayEvents.slice(0, 3).map(renderChip)}
                          {dayEvents.length > 3 && (
                            <div className="text-[10px] text-slate-500 pl-1.5">+{dayEvents.length - 3}</div>
                          )}
                        </>
                      )}
                    </div>
                  </>
                )}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mt-4 text-[11px] text-slate-600">
        {user?.role === "student" ? (
          <>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3.5 h-3.5 rounded bg-slate-100 ring-1 ring-slate-200" />
              {t("cycle_past")}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3.5 h-3.5 rounded bg-brand-blue/15 ring-1 ring-brand-blue/30" />
              {t("cycle_current_lbl")}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3.5 h-3.5 rounded bg-purple-50 ring-1 ring-purple-200" />
              {t("cycle_future")}
            </span>
            <span className="flex items-center gap-1.5"><ClipboardList className="h-3.5 w-3.5 text-orange-600" /> {t("homework_due")}</span>
          </>
        ) : (
          <>
            <span className="flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5 text-brand-blue" /> {t("scheduled_class")}</span>
            <span className="flex items-center gap-1.5"><CalendarCheck className="h-3.5 w-3.5 text-emerald-600" /> {t("attendance_reported")}</span>
            <span className="flex items-center gap-1.5"><ClipboardList className="h-3.5 w-3.5 text-orange-600" /> {t("homework_due")}</span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3.5 h-3.5 bg-red-100 ring-1 ring-red-300 rounded-sm" />
              <span className="text-red-700 font-medium">!</span> {t("missing_report")}
            </span>
          </>
        )}
      </div>

      {/* Selected day side panel */}
      {selectedDay && (
        <Card className="mt-5 p-5" data-testid="calendar-day-detail">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg font-semibold text-slate-900">{selectedDay}</h3>
            <Button variant="ghost" size="sm" onClick={() => setSelectedDay(null)}>{t("close")}</Button>
          </div>
          {selectedEvents.length === 0 ? (
            <p className="text-sm text-slate-500">{t("no_events_for_day")}</p>
          ) : (
            <div className="space-y-2">
              {selectedEvents.map((e, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-md border border-slate-200" data-testid={`calendar-detail-event-${i}`}>
                  {e.type === "class" && <BookOpen className="h-4 w-4 text-brand-blue flex-shrink-0 mt-0.5" />}
                  {e.type === "attendance" && <CalendarCheck className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />}
                  {e.type === "homework" && <ClipboardList className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />}
                  {e.type === "live_session" && <Radio className="h-4 w-4 text-teal-600 flex-shrink-0 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    {e.type === "class" && (
                      <div>
                        <div className="text-sm font-medium text-slate-900">{e.group_name}</div>
                        <div className="text-xs text-slate-500">
                          {t("scheduled_class")}
                          {e.schedule_start_time && ` · ${e.schedule_start_time}${e.schedule_end_time ? `–${e.schedule_end_time}` : ""}`}
                        </div>
                      </div>
                    )}
                    {e.type === "homework" && (
                      <div>
                        <div className="text-sm font-medium text-slate-900">{e.title}</div>
                        <div className="text-xs text-slate-500">
                          {e.group_name} · {e.kind === "due" ? t("homework_due") : t("homework_assigned")}
                          {e.completed && ` · ✓ ${t("completed")}`}
                        </div>
                        {e.link && (
                          <a href={e.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-brand-blue hover:underline mt-1">
                            <ExternalLink className="h-3 w-3" /> {t("open_link")}
                          </a>
                        )}
                      </div>
                    )}
                    {e.type === "live_session" && (
                      <div>
                        <div className="text-sm font-medium text-slate-900">{e.title}</div>
                        <div className="text-xs text-slate-500">
                          WSS Live · {e.start_time}{e.end_time ? `–${e.end_time}` : ""}
                          {e.teacher_name && ` · ${e.teacher_name}`}
                          {(e.levels || []).length ? ` · ${e.levels.join(", ")}` : ` · ${t("live_all_levels")}`}
                        </div>
                        {e.my_booking_status === "waitlisted" && (
                          <div className="text-xs text-amber-700 mt-0.5">{t("live_waitlist")}</div>
                        )}
                        <Link to="/dashboard/live-classes" className="inline-flex items-center gap-1 text-xs text-brand-blue hover:underline mt-1"
                          data-testid={`calendar-detail-live-link-${i}`}>
                          <ExternalLink className="h-3 w-3" /> {t("live_classes")}
                        </Link>
                      </div>
                    )}
                    {e.type === "attendance" && (
                      <div>
                        <div className="text-sm font-medium text-slate-900">{e.group_name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {e.my_status ? (
                            <>{t("my_status")}: <strong>{t(e.my_status)}</strong></>
                          ) : (
                            Object.entries(e.counts || {})
                              .filter(([, v]) => v > 0)
                              .map(([k, v]) => `${t(k)}: ${v}`)
                              .join(" · ")
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
      </>
      )}
    </div>
  );
}
